/*
selectRedactionItems.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import {
  useEffect,
  useState,
  lazy,
  useCallback,
  useDeferredValue,
  useMemo,
  memo,
  useRef,
  MouseEvent,
  ChangeEvent
} from 'react'
import XrxCheckbox from '../../components/xrx-checkbox'
import type { ScrollWrapperRef } from '../../components/scrollWrapper'
import { useNavigate } from 'react-router'
const Card = lazy(()=> import('../../components/Card')) 
import { useTranslation } from 'react-i18next'
import { useRedactionContext } from '../../providers/redactionProvider'
import { ProfileStandardField } from '../../modules/profileStandardField'
import XrxInput from '../../components/xrx-input'
import { Spinner } from '../../components/Spinner'
import {
  getStandardRedactionItems
} from '../../modules/httpClients/presetClient'
import { deviceLanguage } from '../../i18n'
import { Tooltip, TooltipAnchor } from '../../components/Tooltip/tooltip'
import { errorTypes } from '../../constants/errorTypes'
import { useBaseContext } from '../../providers/baseProvider'
import ScrollWrapperLoader from '../../components/scrollWrapperLoader'

interface RedactionItemRowProps {
  item: ProfileStandardField
  isChecked: boolean
  onToggle: (item: ProfileStandardField) => void
  onShowTooltip: (event: MouseEvent<HTMLElement>, text: string) => void
}

type IndexedStandardField = ProfileStandardField & { displayTextLower: string }

const RedactionItemRow = memo((props: RedactionItemRowProps) => {
  const { item, isChecked, onToggle, onShowTooltip } = props
  return (
    <li
      className='list-group-item divider'
      tabIndex={0}
    >
      <div className='left-object'>
        <XrxCheckbox
          className='w-56'
          id={`standard-redaction-checkbox-${item.googleId}`}
          label=''
          isChecked={isChecked}
          onChange={() => onToggle(item)}
        />
      </div>
      <div className='content-container'>
        <div className='left-label'>
          <span>{item.displayText}</span>
        </div>
        <div className='right-object borderless-button ml-0 redaction-item-tooltip'>
          <i
            className='xgl-details_two_tone xglsize-32'
            onClick={event => onShowTooltip(event, item.descriptionText)}
          ></i>
        </div>
      </div>
       {/* <div className='right-object col-1 pr-0 borderless-button ml-0 redaction-item-tooltip'>
<i
            className='xgl-details_two_tone xglsize-32'
            onClick={event => onShowTooltip(event, item.descriptionText)}
          ></i>
       </div> */}
    </li>
  )
})

export default function SelectRedactionItems () {
  const navigate = useNavigate()
  const [availableRedactionItems, setAvailableRedactionItems] = useState<
    IndexedStandardField[]
  >([])
  const [loading, setLoading] = useState(false)
  const [searchInput, setSearchInput] = useState<string>('')
  const [tooltipAnchor, setTooltipAnchor] = useState<TooltipAnchor | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [scrollTop, setScrollTop] = useState(0)
  const [rowHeight, setRowHeight] = useState(48)
  const [containerHeight, setContainerHeight] = useState(0)
  const scrollRef = useRef<ScrollWrapperRef | null>(null)
  const listContainerRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)
  const lastScrollTopRef = useRef(0)
  const redactionContext = useRedactionContext()
  const { setError } = useBaseContext()
  const {
    draftRedactProfile,
    setDraftRedactProfile
  } = redactionContext
  const { t } = useTranslation()
  const handleTooltipAnchorClick = useCallback((
    event: MouseEvent<HTMLElement>,
    text: string
  ) => {
    setTooltipAnchor({
      text: text,
      boundingRect: event.currentTarget.getBoundingClientRect()
    })
  }, [])
  const handleItemToggle = useCallback((redactionItem: ProfileStandardField) => {
    setSelectedIds(prevState => {
      const next = new Set(prevState)
      if (next.has(redactionItem.googleId)) {
        next.delete(redactionItem.googleId)
      } else {
        next.add(redactionItem.googleId)
      }
      return next
    })
  }, [])
  useEffect(() => {
    const initial = draftRedactProfile?.standardFields ?? []
    setSelectedIds(new Set(initial.map(field => field.googleId)))
  }, [draftRedactProfile?.id])
  const _getStandardRedactionItems = useCallback(() => {
    if (!draftRedactProfile) {
      setLoading(false)
      return
    }
    setLoading(true)
    getStandardRedactionItems(draftRedactProfile.country, deviceLanguage)
      .then((r: ProfileStandardField[]) => {
        setAvailableRedactionItems(
          r.map(item => ({
            ...item,
            displayTextLower: item.displayText.toLocaleLowerCase()
          }))
        )
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        setError(errorTypes.presetsError)
      })
  }, [draftRedactProfile?.country])
  useEffect(() => {
    // setLoading(true);
    _getStandardRedactionItems()
  }, [])
  const deferredSearchInput = useDeferredValue(searchInput)
  const normalizedSearchInput = useMemo(
    () => deferredSearchInput.trim().toLocaleLowerCase(),
    [deferredSearchInput]
  )
  const filteredRedactionItems = useMemo(() => {
    if (!normalizedSearchInput) {
      return availableRedactionItems
    }
    return availableRedactionItems.filter(item =>
      item.displayTextLower.includes(normalizedSearchInput)
    )
  }, [availableRedactionItems, normalizedSearchInput])
  const selectedCount = selectedIds.size
  const safeRowHeight = Math.max(rowHeight, 1)
  const totalItems = filteredRedactionItems.length
  const totalHeight = totalItems * safeRowHeight
  const effectiveContainerHeight = Math.max(containerHeight, safeRowHeight)
  const overscan = 6
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / safeRowHeight) - overscan
  )
  const endIndex = Math.min(
    totalItems,
    Math.ceil((scrollTop + effectiveContainerHeight) / safeRowHeight) + overscan
  )
  const visibleItems = filteredRedactionItems.slice(startIndex, endIndex)
  const topPadding = startIndex * safeRowHeight
  const bottomPadding = Math.max(
    0,
    totalHeight - topPadding - visibleItems.length * safeRowHeight
  )
  useEffect(() => {
    const updateContainerHeight = () => {
      if (!listContainerRef.current) {
        return
      }
      setContainerHeight(listContainerRef.current.clientHeight)
    }
    updateContainerHeight()
    window.addEventListener('resize', updateContainerHeight)
    return () => {
      window.removeEventListener('resize', updateContainerHeight)
    }
  }, [])
  useEffect(() => {
    if (!listRef.current) {
      return
    }
    const firstRow = listRef.current.querySelector('li')
    if (!firstRow) {
      return
    }
    const nextHeight = Math.round(firstRow.getBoundingClientRect().height)
    if (nextHeight > 0 && nextHeight !== rowHeight) {
      setRowHeight(nextHeight)
    }
  }, [visibleItems.length, rowHeight])
  useEffect(() => {
    setScrollTop(0)
    lastScrollTopRef.current = 0
    scrollRef.current?.scrollTo(0)
  }, [normalizedSearchInput, availableRedactionItems.length])
  const handleListScroll = useCallback((details: { y: number }) => {
    const nextScrollTop = Math.max(0, -details.y)
    const hasMoved = Math.abs(nextScrollTop - lastScrollTopRef.current) > 1
    if (hasMoved) {
      setTooltipAnchor(prev => (prev ? null : prev))
    }
    lastScrollTopRef.current = nextScrollTop
    setScrollTop(prev => (prev === nextScrollTop ? prev : nextScrollTop))
  }, [])
  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value)
  }, [])
  const handleSearchClear = useCallback(() => {
    setSearchInput('')
  }, [])
  const scrollOptions = useMemo(() => ({ probeType: 3 }), [])
  const handleCancel = () => {
    navigate('/redactionItems')
  }
  const handleOk = () => {
    if (draftRedactProfile) {
      const existingToggleState = new Map(
        draftRedactProfile.standardFields.map(field => [field.googleId, field.isToggled])
      )
      const selectedProfileIds = new Set(
        draftRedactProfile.standardFields.map(field => field.googleId)
      )
      const availableById = new Map(
        availableRedactionItems.map(item => [item.googleId, item])
      )
      const selectedStandardFieldsFromProfile = draftRedactProfile.standardFields
        .filter(field => selectedIds.has(field.googleId))
        .map(field => {
          const item = availableById.get(field.googleId)
          return {
            id: item?.id ?? field.id,
            googleId: field.googleId,
            displayText: item?.displayText ?? field.displayText,
            country: item?.country ?? field.country,
            descriptionText: item?.descriptionText ?? field.descriptionText,
            isToggled: existingToggleState.get(field.googleId) ?? field.isToggled
          }
        })
      const addedStandardFields = availableRedactionItems
        .filter(item => selectedIds.has(item.googleId) && !selectedProfileIds.has(item.googleId))
        .map(item => ({
          id: item.id,
          googleId: item.googleId,
          displayText: item.displayText,
          country: item.country,
          descriptionText: item.descriptionText,
          isToggled: existingToggleState.get(item.googleId) ?? item.isToggled
        }))
      const selectedStandardFields = [
        ...selectedStandardFieldsFromProfile,
        ...addedStandardFields
      ]
      const updatedProfile = {
        ...draftRedactProfile,
        standardFields: selectedStandardFields
      }
      setDraftRedactProfile(updatedProfile)
      navigate('/redactionItems')
    }
  }
  // const handleSelectAllToggle = () => {
  //   const newChecked = !selectAll
  //   if (newChecked) {
  //     const standardFieldNotSelected = [
  //       ...draftRedactProfile.StandardFields
  //     ].filter(c => selectedRedactionItems.indexOf(c) < 0)
  //     setSelectedRedactionItems(prevState => {
  //       return [...prevState, ...standardFieldNotSelected]
  //     })
  //   } else {
  //     setSelectedRedactionItems([])
  //   }
  //   setSelectAll(newChecked)
  // }

  return (
    <>
      <Card
        title={t('EDIT_REDACTION_ITEMS.STANDARD_TITLE')}
        onOk={handleOk}
        onCancel={handleCancel}
        XPadding='none'
      >
        <div className='row justify-content-center mx-3_75 d-flex align-items-end'>
          <div className='col-4 px-0 list-group-item'>
            <div className='left-object'>
              <span className='table-header count'>
                {t('EDIT_REDACTION_ITEMS.NUMBER_SELECTED', {
                  numberSelected: selectedCount,
                  numberTotal: availableRedactionItems.length
                })}
              </span>
            </div>
          </div>
          <div className='col-8 pt-2 px-0 text-right'>
            <XrxInput
              id='search-redaction-items'
              inputType='clearable'
              placeholder={t('EDIT_REDACTION_ITEMS.SEARCH')}
              label=''
              value={searchInput}
              onChange={handleSearchChange}
              // onBlur={() => validateEmail(inputEmail)}
              ariaLabel='Email'
              onClear={handleSearchClear}
              autoFocus={false}
              valid={true}
              validationMessages={[]}
            ></XrxInput>
          </div>
        </div>
        {loading ? (
          <div className='multi-list-group checkbox-selection-list d-flex align-items-center justify-content-center'>
            <Spinner size={170} text={t('LOADING')} />
          </div>
        ) : (
          availableRedactionItems &&
          availableRedactionItems.length > 0 && (
            <div
              className='multi-list-group checkbox-selection-list'
              ref={listContainerRef}
            >
              <ScrollWrapperLoader
                scrollId='select-standard-redaction-items-scroller'
                onScroll={handleListScroll}
                ref={scrollRef}
                options={scrollOptions}
                autoRefreshOnChildrenChange={false}
              >
                <ul
                  className='xrx-list-group'
                  ref={listRef}
                  style={{ paddingTop: topPadding, paddingBottom: bottomPadding }}
                >
                  {visibleItems.map(r => (
                    <RedactionItemRow
                      key={r.googleId}
                      item={r}
                      isChecked={selectedIds.has(r.googleId)}
                      onToggle={handleItemToggle}
                      onShowTooltip={handleTooltipAnchorClick}
                    />
                  ))}
                </ul>
                <Tooltip
                  anchor={tooltipAnchor}
                ></Tooltip>
              </ScrollWrapperLoader>
            </div>
          )
        )}
      </Card>
    </>
  )
}
